import React, { useMemo, useState } from 'react';
import { useGetBuslineSchedulesInfinite } from '@area-restrita/api-client/endpoints/busline-schedules/busline-schedules.gen';
import { GetBuslineSchedulesOrder } from '@area-restrita/api-client/model/getBuslineSchedulesOrder';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDebouncedState } from '@/hooks/useDebounce';

import type { NpsSchedule } from './types';

const NPS_SCHEDULE_SELECTOR_LIMIT = 20;
const NPS_SCHEDULE_SEARCH_DEBOUNCE_MS = 300;

interface ScheduleStepProps {
  initialSchedules: NpsSchedule[];
  onClose: () => void;
  onContinue: (schedules: NpsSchedule[]) => void;
}

const ScheduleStep: React.FC<ScheduleStepProps> = ({
  initialSchedules,
  onClose,
  onContinue,
}) => {
  const [debouncedSearch, setSearch, search] = useDebouncedState(
    '',
    NPS_SCHEDULE_SEARCH_DEBOUNCE_MS,
  );
  const [selectedSchedules, setSelectedSchedules] =
    useState<NpsSchedule[]>(initialSchedules);
  const [validationError, setValidationError] = useState<string | null>(null);
  const schedulesQuery = useGetBuslineSchedulesInfinite(
    {
      page: 1,
      limit: NPS_SCHEDULE_SELECTOR_LIMIT,
      order: GetBuslineSchedulesOrder.favorite,
      search: debouncedSearch.trim() || undefined,
    },
    {
      query: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _, lastPageParam) =>
          lastPage.data.length > 0 ? lastPageParam + 1 : null,
      },
    },
  );
  const selectedIds = useMemo(
    () => new Set(selectedSchedules.map(({ id }) => id)),
    [selectedSchedules],
  );
  const schedules = useMemo(
    () => schedulesQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [schedulesQuery.data?.pages],
  );

  const toggleSchedule = (schedule: NpsSchedule) => {
    setValidationError(null);
    setSelectedSchedules((current) => {
      if (current.some(({ id }) => id === schedule.id)) {
        return current.filter(({ id }) => id !== schedule.id);
      }
      if (current.length >= NPS_SCHEDULE_SELECTOR_LIMIT) return current;
      return [...current, schedule];
    });
  };

  const continueToResponse = () => {
    if (selectedSchedules.length === 0) {
      setValidationError('Selecione pelo menos um horário para continuar.');
      return;
    }

    setValidationError(null);
    onContinue(selectedSchedules);
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Escolha linhas e horários</DialogTitle>
        <DialogDescription>
          Selecione os serviços relacionados à sua experiência.
        </DialogDescription>
      </DialogHeader>
      <Label htmlFor="nps-schedule-search">Buscar linha ou horário</Label>
      <Input
        id="nps-schedule-search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Buscar linha ou horário"
      />
      <p className="text-sm text-muted-foreground">
        {selectedSchedules.length}/{NPS_SCHEDULE_SELECTOR_LIMIT} selecionados
      </p>
      {schedulesQuery.isError ? (
        <Alert variant="destructive">
          <AlertDescription>
            Não foi possível carregar os horários. Tente novamente.
            <Button
              className="ml-2"
              size="sm"
              variant="outline"
              onClick={() => schedulesQuery.refetch()}
            >
              Tentar novamente
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <div className="max-h-72 space-y-2 overflow-y-auto rounded-md border p-2">
          {schedulesQuery.isLoading ? (
            <p className="p-3 text-sm">Carregando horários...</p>
          ) : null}
          {schedules.map((schedule) => (
            <Label
              key={schedule.id}
              className="flex cursor-pointer items-center gap-3 rounded-md p-3 hover:bg-muted"
            >
              <Checkbox
                checked={selectedIds.has(schedule.id)}
                disabled={
                  !selectedIds.has(schedule.id) &&
                  selectedSchedules.length >= NPS_SCHEDULE_SELECTOR_LIMIT
                }
                onCheckedChange={() => toggleSchedule(schedule)}
                aria-label={`${schedule.code} - ${schedule.name}, ${schedule.startAt} - ${schedule.endAt}`}
              />
              <span>
                <span className="block">
                  {schedule.code} - {schedule.name}
                </span>
                <span className="block text-muted-foreground">
                  {schedule.startAt} - {schedule.endAt}
                </span>
              </span>
            </Label>
          ))}
          {schedulesQuery.hasNextPage ? (
            <Button
              className="w-full"
              variant="ghost"
              loading={schedulesQuery.isFetchingNextPage}
              onClick={() => schedulesQuery.fetchNextPage()}
            >
              Carregar mais horários
            </Button>
          ) : null}
        </div>
      )}
      {validationError ? (
        <p role="alert" className="text-sm text-destructive">
          {validationError}
        </p>
      ) : null}
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Responder depois
        </Button>
        <Button onClick={continueToResponse}>Confirmar horários</Button>
      </DialogFooter>
    </>
  );
};

export default ScheduleStep;
